/**
 * Module List Component
 *
 * Displays a list of modules in table format
 * Pure presentation component
 */

import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ContactModuleDetail } from '../../core/models';
import { TranslatePipe } from '../../core/features/translations';

@Component({
  selector: 'app-module-list',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="w-full overflow-x-auto bg-white rounded border border-gray-200 shadow-sm sm:shadow-none sm:border-0 custom-scrollbar">
      <table class="w-full border-collapse text-sm text-left table-fixed">
        <thead>
          <tr class="bg-gray-50 border-b-2 border-gray-200">
            <th class="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[32%]">{{ 'portal.degrees.modules.table.name' | translate }}</th>
            <th class="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[23%]">{{ 'portal.degrees.modules.table.period' | translate }}</th>
            <th class="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[17%]">{{ 'portal.degrees.modules.table.location' | translate }}</th>
            <th class="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[14%]">{{ 'portal.degrees.attendance.title' | translate }}</th>
            <th class="px-4 py-3 font-semibold text-gray-600 uppercase tracking-wider text-[0.7rem] w-[14%]">{{ 'portal.degrees.modules.table.status' | translate }}</th>
          </tr>
        </thead>
        <tbody class="divide-y divide-gray-100">
          @for (module of modules(); track module.contact_module_id) {
            <tr 
              class="hover:bg-gray-50 transition-all duration-150 border-l-[3px]"
              [class.border-green-500]="cardClass() === 'current'"
              [class.border-blue-500]="cardClass() === 'upcoming'"
              [class.border-gray-400]="cardClass() === 'past'"
            >
              <td class="px-4 py-4 align-top">
                <div class="flex flex-wrap gap-2 items-center">
                  <span class="font-bold text-gray-900 text-[0.95rem] leading-snug">{{ module.module_name }}</span>
                  @if (module.variant_code) {
                    <span class="px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[0.7rem] font-bold border border-blue-100">{{ module.variant_code }}</span>
                  }
                  @if (module.module_code) {
                    <span class="px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[0.7rem] font-bold border border-purple-100">{{ module.module_code }}</span>
                  }
                </div>
              </td>
              <td class="px-4 py-4 align-top">
                <div class="flex items-center gap-2 text-gray-600">
                  <i class="pi pi-calendar text-[0.85rem] text-blue-500"></i>
                  <span class="whitespace-nowrap font-medium">{{ formatDate(module.start_date) }} - {{ formatDate(module.end_date) }}</span>
                </div>
              </td>
              <td class="px-4 py-4 align-top">
                <div class="flex items-start gap-2 text-gray-600 font-medium">
                  <i class="pi pi-map-marker text-[0.85rem] text-red-400 mt-0.5"></i>
                  <span class="line-clamp-2 leading-relaxed">{{ module.location_shortname || module.location_name || '-' }}</span>
                </div>
              </td>
              <td class="px-4 py-4 align-top">
                @if (module.total_lectures && module.total_lectures > 0) {
                  <div class="flex flex-col gap-0.5">
                    <span 
                      class="inline-flex items-center justify-center px-2.5 py-0.5 rounded text-[0.75rem] font-bold shadow-sm"
                      [class.bg-emerald-50]="(module.attendance_rate ?? 0) >= 80"
                      [class.text-emerald-700]="(module.attendance_rate ?? 0) >= 80"
                      [class.border]="true"
                      [class.border-emerald-200]="(module.attendance_rate ?? 0) >= 80"
                      [class.bg-amber-50]="(module.attendance_rate ?? 0) >= 70 && (module.attendance_rate ?? 0) < 80"
                      [class.text-amber-700]="(module.attendance_rate ?? 0) >= 70 && (module.attendance_rate ?? 0) < 80"
                      [class.border-amber-200]="(module.attendance_rate ?? 0) >= 70 && (module.attendance_rate ?? 0) < 80"
                      [class.bg-rose-50]="(module.attendance_rate ?? 0) < 70"
                      [class.text-rose-700]="(module.attendance_rate ?? 0) < 70"
                      [class.border-rose-200]="(module.attendance_rate ?? 0) < 70"
                      [title]="(module.attended_lectures || 0) + ' / ' + (module.total_lectures || 0) + ' (' + ('portal.degrees.attendance.attended' | translate) + ')'"
                    >
                      {{ module.attendance_rate !== null && module.attendance_rate !== undefined ? module.attendance_rate + '%' : '-' }}
                    </span>
                    <span class="text-[0.68rem] text-gray-400 font-medium whitespace-nowrap">
                      {{ module.attended_lectures || 0 }}/{{ module.total_lectures || 0 }} {{ 'portal.degrees.attendance.lectures_short' | translate }}
                    </span>
                  </div>
                } @else {
                  <span class="text-xs text-gray-400 font-medium">-</span>
                }
              </td>
              <td class="px-4 py-4 align-top">
                <div class="flex flex-wrap gap-1.5">
                  <span 
                    class="inline-flex items-center px-2.5 py-0.5 rounded text-[0.75rem] font-semibold text-white shadow-sm transition-transform hover:scale-105"
                    [class.bg-green-600]="getStatusClass(module.enrollment_status_name) === 'status-garantiert'"
                    [class.bg-orange-500]="getStatusClass(module.enrollment_status_name) === 'status-geplant'"
                    [class.bg-red-500]="getStatusClass(module.enrollment_status_name) === 'status-abgesagt'"
                    [class.bg-gray-500]="getStatusClass(module.enrollment_status_name) === 'status-durchgeführt' || getStatusClass(module.enrollment_status_name) === 'status-unknown'"
                    [class.bg-purple-600]="getStatusClass(module.enrollment_status_name) === 'status-pausiert'"
                    [class.bg-slate-500]="!isKnownStatus(module.enrollment_status_name)"
                  >
                    {{ module.enrollment_status_name || 'N/A' }}
                  </span>
                  @if (module.is_dispensed) {
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded text-[0.75rem] font-bold bg-orange-100 text-orange-800 ring-1 ring-orange-200">
                      {{ 'portal.degrees.modules.dispensed' | translate }}
                    </span>
                  }
                  @if (module.is_repeating) {
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded text-[0.75rem] font-bold bg-purple-100 text-purple-800 ring-1 ring-purple-200">
                      {{ 'portal.degrees.modules.repeating' | translate }}
                    </span>
                  }
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    </div>
  `,
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModuleListComponent {
  // Inputs
  modules = input<ContactModuleDetail[]>([]);
  cardClass = input<string>(''); // 'current', 'upcoming', or 'past'

  /**
   * Whether the status name has an explicit background binding in the template
   * (incl. the null/unknown case). Any other name — e.g. "Pausiert" before its
   * explicit mapping or future backend status seeds — receives the generic
   * fallback background so the white label never renders white-on-white
   * without a background.
   */
  isKnownStatus(statusName: string | null | undefined): boolean {
    if (!statusName) return true;
    const statusClass = this.getStatusClass(statusName);
    return (
      statusClass === 'status-garantiert' ||
      statusClass === 'status-geplant' ||
      statusClass === 'status-abgesagt' ||
      statusClass === 'status-durchgeführt' ||
      statusClass === 'status-pausiert'
    );
  }

  /**
   * Get CSS class for enrollment status
   */
  getStatusClass(statusName: string | null | undefined): string {
    if (!statusName) return 'status-unknown';
    return `status-${statusName.toLowerCase().replace(/\s+/g, '-')}`;
  }

  /**
   * Format date to German locale
   */
  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  }
}
